import * as carrierAccountsActions from '~/actions/carrier-accounts.actions';
import { ActionKeys } from '~/actions/constants';
import * as gtmActions from '~/actions/gtm.actions';
import { sendTrackEvent } from '~/actions/prod-analytics.actions';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { CANADA_POST, createEndpointCarriers, DHL_EXPRESS, UPS } from '~/constants/carriers';
import { SET_FIRST_CARRIER } from '~/constants/gtm';
import {
  CANADA_POST_MASTER_REGISTRATION_SUCCESS,
  CARRIER_REGISTRATION_SUCCESS,
  UPS_MASTER_REGISTRATION_SUCCESS,
  DHL_EXPRESS_MASTER_REGISTRATION_SUCCESS,
} from '~/constants/user-events';
import keyBy from 'lodash-es/keyBy';
import { call, put, takeLatest } from 'redux-saga/effects';
import { getUserCarrierAccounts } from '~/selectors/carrier-accounts.selectors';
import * as carrierAccountsService from '~/services/carrier-accounts.service';
import * as Carrier from '~/types/Carrier';
import * as CarrierAccount from '~/types/CarrierAccount';
import * as CarrierAccountRegStatus from '~/types/CarrierAccountRegStatus';
import { selectState } from '..';
import { fetchServiceLevelsByAccount } from '../providers/providers.saga';
import {
  trackRequestedUpdatedAxiosError,
  trackRequestedUpdatedResponse,
} from '~/utils/TrackingHelper/utils';

export function* fetchCarrierAccounts(
  payload: ReturnType<typeof carrierAccountsActions.fetchCarrierAccounts>,
) {
  try {
    const response: AxiosResponse<{ results: CarrierAccount.JSONCarrierAccount[] }> = yield call(
      carrierAccountsService.getCarrierAccounts,
      payload.payload,
    );
    const carrierAccountsResponse = response.data.results.map(CarrierAccount.fromJSON);
    const accountCarriers = carrierAccountsResponse
      .filter((account) => !!account.carrier)
      .map((account) => account.carrier?.providerToken);

    // Fetch and append extra carrier fields from adapter_provider to each account
    const carrierProvidersResponse: AxiosResponse<{
      results: { carriers: Carrier.JSONCarrier[] };
    }> = yield call(carrierAccountsService.getCarrierProviders, accountCarriers);

    const carriersExtraFields = carrierProvidersResponse.data.results.carriers.map(
      Carrier.extraFieldsFromJSON,
    );
    const carriersMap = keyBy(carriersExtraFields, 'providerToken');

    // Join extra fields from carrier provider to carrier accounts
    const carrierAccounts = carrierAccountsResponse
      .filter((account) => account.carrier)
      .map((account) => {
        return {
          ...account,
          carrier: {
            ...account.carrier,
            ...carriersMap[account.carrier.providerToken],
          },
        };
      });

    // Parse response to CarrierAccount objects then filter invalid carriers (currently don't support ShippoCommon) - Vincent
    const parsedCarrierAccounts: carrierAccountsActions.FetchCarrierAccountsSuccessPayload = {
      carrierAccounts: carrierAccounts,
    };

    yield put(carrierAccountsActions.fetchCarrierAccountsSuccess(parsedCarrierAccounts));
  } catch (err) {
    yield put(
      carrierAccountsActions.fetchCarrierAccountsError({ error: (err as AxiosError).message }),
    );
  }
}

export function* createCarrierAccount(
  action: ReturnType<typeof carrierAccountsActions.createCarrierAccount>,
) {
  const { trackingReference } = action.payload;
  try {
    let response: AxiosResponse<CarrierAccount.JSONCarrierAccount>;

    // Currently the /carrier_accounts/register/new endpoint only supports UPS Master
    // The other carrer that supports Create is Canada Post, but has not been migrated to this endpoint
    if (
      createEndpointCarriers.includes(action.payload.carrierAccount.carrier.id) ||
      action.payload.carrierAccount.parameters?.isMasterAccount
    ) {
      const jsonCreateCarrierAccount: CarrierAccount.JSONCreateCarrierAccount =
        CarrierAccount.toCreateJSON(action.payload.carrierAccount);
      response = yield call(carrierAccountsService.createCarrierAccount, jsonCreateCarrierAccount);
    } else {
      // Else not supported, so use legacy endpoint /carrier_accounts to create account
      const jsonCarrierAccount: CarrierAccount.JSONCarrierAccount = CarrierAccount.toJSON(
        action.payload.carrierAccount,
      );
      response = yield call(carrierAccountsService.connectCarrierAccount, jsonCarrierAccount);
    }

    trackRequestedUpdatedResponse(trackingReference, response);

    const carrierAccount = CarrierAccount.fromJSON(response.data)!;

    yield put(
      carrierAccountsActions.createCarrierAccountSuccess({ carrierAccount, trackingReference }),
    );
    yield put(
      sendTrackEvent(CARRIER_REGISTRATION_SUCCESS, {
        Carrier: carrierAccount.carrier.name,
      }),
    );

    // Send specific event for UPS Master
    if (carrierAccount.carrier && carrierAccount.isShippoAccount) {
      switch (carrierAccount.carrier) {
        case UPS:
          yield put(sendTrackEvent(UPS_MASTER_REGISTRATION_SUCCESS));
          break;
        case CANADA_POST:
          yield put(sendTrackEvent(CANADA_POST_MASTER_REGISTRATION_SUCCESS));
          break;
        case DHL_EXPRESS:
          yield put(sendTrackEvent(DHL_EXPRESS_MASTER_REGISTRATION_SUCCESS));
          break;
        default:
          break;
      }
    }
  } catch (err) {
    if (axios.isAxiosError(err)) {
      trackRequestedUpdatedAxiosError(trackingReference, err);
    }

    let errorMessage;
    if (err && (err as AxiosError).response) {
      const responseData = (err as AxiosError).response?.data;
      errorMessage =
        typeof responseData === 'object'
          ? Object.values(responseData).join('\n')
          : Array.isArray(responseData)
            ? responseData.join('\n')
            : responseData;
    } else {
      errorMessage = 'An error occurred. Please contact support.';
    }

    yield put(carrierAccountsActions.createCarrierAccountError({ error: errorMessage }));
  }
}

export function* connectCarrierAccount(
  action: ReturnType<typeof carrierAccountsActions.connectCarrierAccount>,
) {
  const { trackingReference } = action.payload;
  try {
    const jsonCarrierAccount: CarrierAccount.JSONCarrierAccount = CarrierAccount.toJSON(
      action.payload.carrierAccount,
    );

    const carriers: readonly CarrierAccount.CarrierAccount[] =
      yield selectState<readonly CarrierAccount.CarrierAccount[]>(getUserCarrierAccounts);
    const response: AxiosResponse<CarrierAccount.JSONCarrierAccount> = yield call(
      carrierAccountsService.connectCarrierAccount,
      jsonCarrierAccount,
    );

    trackRequestedUpdatedResponse(trackingReference, response);

    const carrierAccount = CarrierAccount.fromJSON(response.data)!;

    yield put(carrierAccountsActions.connectCarrierAccountSuccess({ carrierAccount }));

    // Triggering GTM and Heap for first carrier connect success
    if (carriers.length === 0) {
      yield put(
        gtmActions.sendGTMEvent({
          eventName: SET_FIRST_CARRIER,
        }),
      );

      yield put(sendTrackEvent('First Carrier Connect'));
    }

    yield put(
      sendTrackEvent('Carrier Connect Success', {
        Carrier: carrierAccount.carrier.name,
      }),
    );
  } catch (err) {
    if (axios.isAxiosError(err)) {
      trackRequestedUpdatedAxiosError(trackingReference, err);
    }

    let errorMessage;
    if ((err as AxiosError).response) {
      const responseData = (err as AxiosError).response?.data;
      errorMessage =
        typeof responseData === 'object'
          ? Object.values(responseData).join('\n')
          : Array.isArray(responseData)
            ? responseData.join('\n')
            : responseData;
    } else {
      errorMessage = 'An error occurred. Please contact support.';
    }

    yield put(carrierAccountsActions.connectCarrierAccountError({ error: errorMessage }));
  }
}

export function* editCarrierAccount(
  action: ReturnType<typeof carrierAccountsActions.editCarrierAccount>,
) {
  const { trackingReference } = action.payload;
  try {
    const jsonCarrierAccount: CarrierAccount.JSONCarrierAccount = CarrierAccount.toJSON(
      action.payload.carrierAccount,
    );

    const response: AxiosResponse<CarrierAccount.JSONCarrierAccount> = yield call(
      carrierAccountsService.editCarrierAccount,
      jsonCarrierAccount,
      action.payload.id,
    );
    const carrierAccount = CarrierAccount.fromJSON(response.data)!;

    yield put(carrierAccountsActions.editCarrierAccountSuccess({ carrierAccount }));

    trackRequestedUpdatedResponse(trackingReference, response);
  } catch (err) {
    if (axios.isAxiosError(err)) {
      trackRequestedUpdatedAxiosError(trackingReference, err);
    }

    let errorMessage;
    if ((err as AxiosError).response) {
      const responseData = (err as AxiosError).response?.data;
      errorMessage =
        typeof responseData === 'object'
          ? Object.values(responseData).join('\n')
          : Array.isArray(responseData)
            ? responseData.join('\n')
            : responseData;
    } else {
      errorMessage = 'An error occurred. Please contact support.';
    }

    yield put(carrierAccountsActions.editCarrierAccountError({ error: errorMessage }));
  }
}

export function* editCarrierAccountServiceLevels(
  action: ReturnType<typeof carrierAccountsActions.editCarrierAccountServiceLevels>,
) {
  const { trackingReference } = action.payload;
  try {
    const response: AxiosResponse<unknown> = yield call(
      carrierAccountsService.editCarrierAccountServiceLevels,
      action.payload.serviceLevelsPayload!,
      action.payload.id,
    );

    trackRequestedUpdatedResponse(trackingReference, response);

    yield call(fetchServiceLevelsByAccount);
    yield put(
      carrierAccountsActions.editCarrierAccountServiceLevelsSuccess({ status: response.status }),
    );

    // after successful Service Levels update, call editCarrierAccount() to update Account options
    yield put(
      carrierAccountsActions.editCarrierAccount({
        carrierAccount: action.payload.carrierAccount!,
        id: action.payload.id,
        trackingReference,
      }),
    );
  } catch (err) {
    if (axios.isAxiosError(err)) {
      trackRequestedUpdatedAxiosError(trackingReference, err);
    }

    let errors;

    if ((err as AxiosError).response) {
      const responseData = (err as AxiosError).response?.data;

      errors = responseData.errors || [];

      // if legacy responseData.error (string), wrap error in Array bracket
      if (responseData.error) {
        errors = [responseData.error];
      }
    }

    yield put(carrierAccountsActions.editCarrierAccountServiceLevelsError(errors));
  }
}

export function* fetchCarrierAccountRegStatuses() {
  try {
    const response: AxiosResponse<{
      results: CarrierAccountRegStatus.JSONCarrierAccountRegStatus[];
    }> = yield call(carrierAccountsService.fetchCarrierAccountRegStatuses);

    const carrierAccountRegStatuses = response.data.results.map((regStatus) =>
      CarrierAccountRegStatus.fromJSON(regStatus),
    );

    yield put(
      carrierAccountsActions.fetchCarrierAccountRegStatusesSuccess({ carrierAccountRegStatuses }),
    );
  } catch (err) {
    yield put(
      carrierAccountsActions.fetchCarrierAccountRegStatusesError({
        error: (err as AxiosError).message,
      }),
    );
  }
}

export default [
  takeLatest(ActionKeys.FETCH_CARRIER_ACCOUNTS, fetchCarrierAccounts),
  takeLatest(ActionKeys.CONNECT_CARRIER_ACCOUNT, connectCarrierAccount),
  takeLatest(ActionKeys.EDIT_CARRIER_ACCOUNT, editCarrierAccount),
  takeLatest(ActionKeys.EDIT_CARRIER_ACCOUNT_SERVICE_LEVELS, editCarrierAccountServiceLevels),
  takeLatest(ActionKeys.CREATE_CARRIER_ACCOUNT, createCarrierAccount),
  takeLatest(
    [
      ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES,
      ActionKeys.CREATE_CARRIER_ACCOUNT_SUCCESS,
      ActionKeys.CREATE_CARRIER_ACCOUNT_ERROR,
    ],
    fetchCarrierAccountRegStatuses,
  ),
];
